import { useCallback, useEffect, useState } from "react";
import axios from "axios";

/**
 * Fetch org routes once and return a map of routeId → name.
 * Matches Call Details / Call Routings (route/get-routes).
 */
export default function useOrgRouteNames() {
  const [routeMap, setRouteMap] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.orgId?.S;
        if (!orgId) {
          if (!cancelled) setLoaded(true);
          return;
        }

        const res = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "route/get-routes",
          { org_id: orgId }
        );

        const map = {};
        (res.data?.routes || []).forEach((route) => {
          const id = route.routeId?.S || route.routeId;
          const name = route.name?.S || route.name;
          if (id) {
            map[String(id)] = name ? String(name) : String(id);
          }
        });

        if (!cancelled) setRouteMap(map);
      } catch (err) {
        console.error("useOrgRouteNames: failed to load routes", err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveRouteName = useCallback(
    (routeId) => {
      if (!routeId) return "";
      const id = String(routeId);
      if (routeMap[id]) return routeMap[id];
      // Loaded but missing → deleted / unknown (same as Call Details)
      if (loaded) return "Deleted Route";
      // Still loading — show id briefly rather than flashing "Deleted"
      return id;
    },
    [routeMap, loaded]
  );

  return { routeMap, resolveRouteName, loaded };
}
